import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useClearRefinements,
  useCurrentRefinements,
  useInfiniteHits,
  useInstantSearch,
} from 'react-instantsearch'
import Skeleton from 'react-loading-skeleton'
import { isEmpty } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import * as Accordion from '@radix-ui/react-accordion'
import { useCategoryStore } from '@velasca/hooks/use-category-store'
import type { AttributeForFacetingConfig } from '@velasca/types'
import { Button } from '@velasca/ui/button'
import { trackEvent } from '@velasca/utils/track-event'
import { ActiveRefinements } from './ActiveRefinements'
import { useAlgoliaFilters } from './AlgoliaSearchProvider'
import { FacetValues } from './FacetValues'
import { NewFilter } from './NewFilter'
import {
  AccordionContent,
  AccordionItem,
  Chevron,
  FacetToggle,
  FacetValuesBackground,
  FilterBy,
  FooterButtonWrapper,
  LeftSide,
  MainFilterContainer,
  PanelToggle,
  RelativeContainer,
  RightSide,
  ScrollableArea,
} from './styles'
import { buildPanelsToShow } from './utils'

interface FacetsProps {
  panels: AttributeForFacetingConfig[]
}

export const Facets = ({ panels }: FacetsProps) => {
  const { openPanel, setPanel, setIsSearchActive, setIsMenuOpen } =
    useAlgoliaFilters()
  const isDesktop = useMediaQuery('(min-width: 992px)')
  const { refine: clearRefinements } = useClearRefinements()
  const { t } = useTranslation('collection-filters')
  const { items } = useCurrentRefinements({
    includedAttributes: [
      ...panels.map(({ attribute }) => attribute),
      'staff_pick_icon_id',
    ],
  })
  const { results } = useInfiniteHits()
  const { status } = useInstantSearch()
  const getPanelsRefinements = (attribute: string) => {
    const currentRefinement = items.find((item) => {
      return item.attribute === attribute
    })

    const selectedRefinements = currentRefinement?.refinements.length

    return { selectedRefinements }
  }

  const currentCategory = useCategoryStore((state) => state.currentCategory)

  const isCalculting =
    currentCategory.isMainCollection !== null &&
    currentCategory.isSubCollection !== null &&
    currentCategory.hasShoes !== null

  const panelsToShow =
    (isCalculting ? buildPanelsToShow(panels, currentCategory) : []) || []

  return (
    <RelativeContainer>
      <MainFilterContainer>
        {isDesktop ? (
          isEmpty(panelsToShow) ? (
            <Skeleton width={'100%'} height={'5rem'} />
          ) : (
            <>
              <LeftSide>
                <FilterBy>{t('filterBy')}</FilterBy>
                {panelsToShow.map(
                  ({ handle, attribute, locale, sortBy, limit }) => {
                    const { selectedRefinements } =
                      getPanelsRefinements(attribute)

                    return (
                      <React.Fragment key={handle}>
                        <PanelToggle
                          key={handle}
                          isOpen={openPanel === handle}
                          onClick={() => {
                            if (openPanel !== handle) {
                              trackEvent({
                                googleAnalytics: {
                                  eventName: 'filters_opened',
                                  data: { handle },
                                },
                              })
                            }
                            setPanel(handle)
                            setIsSearchActive(true)
                          }}
                        >
                          {t(locale ?? '-')}{' '}
                          {selectedRefinements
                            ? `(${selectedRefinements})`
                            : ''}{' '}
                          <Chevron $rotate={openPanel === handle} />
                        </PanelToggle>
                        <FacetValuesBackground isOpen={openPanel === handle}>
                          <FacetValues
                            key={`facet-${attribute}`}
                            handle={handle}
                            attribute={attribute}
                            sortBy={sortBy}
                            isOpen={handle === openPanel}
                            loading={status !== 'idle'}
                            limit={limit}
                          />
                          <ActiveRefinements isSticky={false} />
                        </FacetValuesBackground>
                      </React.Fragment>
                    )
                  },
                )}
                <NewFilter />
              </LeftSide>
              {!isEmpty(items) && status === 'idle' && (
                <RightSide>
                  {t('results', { count: results?.nbHits })}
                </RightSide>
              )}
            </>
          )
        ) : (
          <>
            <div>
              <FilterBy>{t('filterBy')}</FilterBy>
            </div>
            <LeftSide>
              <Accordion.Root asChild collapsible type="single">
                <ScrollableArea>
                  {panelsToShow.map(
                    ({ handle, attribute, locale, sortBy, limit }) => {
                      const { selectedRefinements } =
                        getPanelsRefinements(attribute)

                      return (
                        <Accordion.Item asChild key={handle} value={handle}>
                          <AccordionItem>
                            <FacetToggle>
                              <div>
                                {t(locale ?? '-')}{' '}
                                {selectedRefinements
                                  ? `(${selectedRefinements})`
                                  : ''}{' '}
                              </div>
                              <Chevron />
                            </FacetToggle>
                            <AccordionContent forceMount>
                              <FacetValues
                                handle={handle}
                                attribute={attribute}
                                sortBy={sortBy}
                                loading={status !== 'idle'}
                                limit={limit}
                              />
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion.Item>
                      )
                    },
                  )}
                  <NewFilter hasNeutralColor />
                </ScrollableArea>
              </Accordion.Root>
            </LeftSide>
            <FooterButtonWrapper>
              <Button
                variant="primary"
                text={t('filterButton_showProducts')}
                onClick={() => {
                  setIsMenuOpen(false)
                }}
              ></Button>
              <Button
                variant="outline-dark"
                text={t('clearFilters')}
                onClick={() => {
                  clearRefinements()
                }}
              ></Button>
            </FooterButtonWrapper>
          </>
        )}
      </MainFilterContainer>
    </RelativeContainer>
  )
}
